/**
 * Helpers over the dataset catalog returned by GET /reports/datasets.
 *
 * The catalog is a tree: a dataset has root fields, nested relation nodes
 * (many-to-one, traversable to any depth) and one-to-many nodes that expose
 * rollups plus filter-only fields. indexDataset() flattens that tree so the
 * builder can look up any reference string in O(1).
 */

export const TEMPORAL_TYPES = new Set(['date', 'datetime']);
export const NUMERIC_TYPES = new Set(['integer', 'number', 'money']);

/** Split "path.field:bucket" into { base, bucket }. */
export function splitRef(ref) {
  const [base, bucket] = String(ref || '').split(':');
  return { base, bucket: bucket || null };
}

export function indexDataset(dataset) {
  const fieldIndex = new Map();
  const measureIndex = new Map();
  const rollupIndex = new Map();
  const dateFields = [];

  if (!dataset) {
    return { fieldIndex, measureIndex, rollupIndex, dateFields };
  }

  const addField = (f, labels, path, viaOneToMany) => {
    const entry = {
      ...f,
      labels,
      path,
      filterOnly: !!f.filter_only,
      viaOneToMany,
      breadcrumb: [...labels, f.label].join(' › '),
    };
    fieldIndex.set(f.id, entry);
    if (TEMPORAL_TYPES.has(f.type) && !viaOneToMany && !f.filter_only) {
      dateFields.push({ id: f.id, label: entry.breadcrumb });
    }
  };

  (dataset.fields || []).forEach((f) => addField(f, [], '', false));

  const walk = (node, labels, viaOneToMany) => {
    const nextLabels = [...labels, node.label];
    const o2m = viaOneToMany || node.cardinality === 'one_to_many';
    (node.fields || []).forEach((f) => addField(f, nextLabels, node.path, o2m));
    (node.rollups || []).forEach((r) => {
      const entry = { ...r, labels: nextLabels, path: node.path, isRollup: true, breadcrumb: [...nextLabels, r.label].join(' › ') };
      rollupIndex.set(r.id, entry);
      fieldIndex.set(r.id, { ...entry, filterOnly: false, viaOneToMany: false, dimension: false, sortable: true, filterable: true });
    });
    (node.relations || []).forEach((child) => walk(child, nextLabels, o2m));
  };
  (dataset.relations || []).forEach((rel) => walk(rel, [], false));

  (dataset.measures || []).forEach((m) => measureIndex.set(m.id, { ...m, breadcrumb: m.label }));

  // Put the dataset's own date field first so it is the default choice.
  if (dataset.date_field) {
    dateFields.sort((a, b) => (a.id === dataset.date_field ? -1 : b.id === dataset.date_field ? 1 : 0));
  }
  return { fieldIndex, measureIndex, rollupIndex, dateFields };
}

const BUCKET_LABEL = { day: 'day', week: 'week', month: 'month', year: 'year' };

/** Human label for any reference, or a "missing" marker when it is not in the catalog. */
export function labelFor(index, ref) {
  const { base, bucket } = splitRef(ref);
  const entry = index?.fieldIndex?.get(base);
  if (!entry) {
    return { label: ref, breadcrumb: ref, missing: true, entry: null };
  }
  const suffix = bucket ? ` (${BUCKET_LABEL[bucket] || bucket})` : '';
  return { label: `${entry.label}${suffix}`, breadcrumb: `${entry.breadcrumb}${suffix}`, missing: false, entry };
}

export function measureLabelFor(index, measureSpec) {
  if (measureSpec.id) {
    const m = index?.measureIndex?.get(measureSpec.id);
    return m ? { label: m.label, missing: false } : { label: measureSpec.id, missing: true };
  }
  const f = labelFor(index, measureSpec.field);
  const aggLabel = { sum: 'Σ', avg: 'Avg', min: 'Min', max: 'Max', count: 'Count', count_distinct: 'Distinct' }[measureSpec.agg] || measureSpec.agg;
  return { label: `${aggLabel} ${f.breadcrumb}`, missing: f.missing };
}

/** Output column id the backend will use for a measure spec. */
export function measureAlias(spec) {
  if (spec.alias) return spec.alias;
  if (spec.id) return spec.id;
  return `${spec.agg}_${String(spec.field || '').replace(/\./g, '_')}`;
}

/** Reference string for a group-by spec ({field, bucket}). */
export function groupRef(g) {
  return g.bucket ? `${g.field}:${g.bucket}` : g.field;
}

/**
 * Return a pruned copy of the dataset tree keeping only nodes whose label or
 * breadcrumb matches `query` (case-insensitive). Ancestors of matches are kept.
 */
export function filterTree(dataset, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!dataset) return null;
  if (!q) return dataset;
  const match = (text) => String(text || '').toLowerCase().includes(q);

  const pruneNode = (node, labels) => {
    const nextLabels = [...labels, node.label];
    const crumb = nextLabels.join(' › ');
    const fields = (node.fields || []).filter((f) => match(f.label) || match(`${crumb} › ${f.label}`));
    const rollups = (node.rollups || []).filter((r) => match(r.label) || match(`${crumb} › ${r.label}`));
    const relations = (node.relations || []).map((c) => pruneNode(c, nextLabels)).filter(Boolean);
    if (!fields.length && !rollups.length && !relations.length && !match(node.label)) return null;
    return { ...node, fields, rollups, relations, forceOpen: true };
  };

  return {
    ...dataset,
    fields: (dataset.fields || []).filter((f) => match(f.label)),
    relations: (dataset.relations || []).map((r) => pruneNode(r, [])).filter(Boolean),
    measures: (dataset.measures || []).filter((m) => match(m.label)),
  };
}
